import React, { useState, useRef } from 'react';

function ImageUploader({ initialImage = null, onImageChange, maxSizeMB = 5, label = "Upload Image" }) {
  const [preview, setPreview] = useState(initialImage);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  // Convert MB to bytes
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    const dt = e.dataTransfer;
    const files = dt.files;
    
    if (files && files.length === 1) {
      processFile(files[0]);
    }
  };

  // Process the selected file
  const processFile = (file) => {
    setError('');
    
    if (!file) {
      return;
    }
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError('File must be an image (JPEG, PNG, or GIF).');
      return;
    }
    
    // Check file size
    if (file.size > maxSizeBytes) {
      setError(`File is too large. Max size is ${maxSizeMB}MB.`);
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      
      // Call the callback with the selected file
      if (onImageChange) {
        onImageChange(file, reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle click on the upload area
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Remove the current image
  const removeImage = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageChange) {
      onImageChange(null, null);
    }
  };

  return (
    <div className="mt-1">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg, image/png, image/gif"
        className="sr-only"
      />
      
      {/* Upload area */}
      <div
        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${
          dragging ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
        } ${!preview ? 'border-dashed' : ''} rounded-md cursor-pointer`}
        onClick={preview ? () => {} : handleUploadClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {preview ? (
          <div className="w-full relative">
            <img 
              src={preview} 
              alt="Preview" 
              className="mx-auto max-h-64 object-contain"
            />
            <div className="flex justify-center mt-3 space-x-4">
              <button
                type="button"
                onClick={handleUploadClick}
                className="px-3 py-1 text-sm text-primary-600 hover:text-primary-900 font-medium"
              >
                Change
              </button>
              <button
                type="button"
                onClick={removeImage}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-900 font-medium"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-gray-600 justify-center">
              <label className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                <span>{label}</span>
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF up to {maxSizeMB}MB
            </p>
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}

export default ImageUploader;
